import { Mode } from 'light-component';
import { useMemo, type ReactElement } from 'react';
import { lang } from 'zf-infra';
import { Block } from '../../../../../block';
import { useTranslation } from '../../../../../i18n';
import { service } from '../service';
import styles from '../style.module.less';
import Calculator from '../calculator';
import { RowAction } from './action';
import type { CellSetting } from '../types';
import type { TBodyProps } from './types';

export function TBody(props: TBodyProps): ReactElement {
  const [t] = useTranslation();

  const rows = useMemo(() => props.data.map((row, rowIndex) => row.map((column, colIndex) => ({
    ...column,
    field: `${String.fromCharCode(64 + colIndex + 1)}${rowIndex + 1}`
  }))), [props.data]);

  const computeDefined = useMemo(() => {
    const computeDefined: Record<string, Calculator> = {};
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
      const row = rows[rowIndex];
      for (let colIndex = 0; colIndex < row.length; colIndex += 1) {
        const column = row[colIndex];
        if (column.fx != null && column.fx !== '') {
          computeDefined[column.field] = new Calculator(column.fx);
        }
      }
    }
    return computeDefined;
  }, [rows]);

  const rowData = useMemo(() => {
    const rowData: Record<string, string | number> = {};
    for (const row of rows) {
      for (const column of row) {
        if (column.title != null) {
          rowData[column.field] = column.title;
        }
      }
    }
    return rowData;
  }, [rows]);

  function handleChange(column: CellSetting, value: string, rowIndex: number, colIndex: number): void {
    let cellValue = value;
    if (column.type === 'number') {
      cellValue = checkNumberValid(value);
    }

    const rows = lang.clone(props.data);
    const col = rows[rowIndex][colIndex];
    col.title = cellValue;
    rowData[col.field] = cellValue;

    Object.entries(computeDefined).forEach(([field, computeDefinition]) => {
      const computed = computeDefinition.compute(rowData);
      const rowIndex = parseInt(field.slice(1), 10) - 1;
      const colIndex = (field.charCodeAt(0) - 64) - 1;
      const col = rows[rowIndex][colIndex];
      if (computed == null) {
        col.title = '';
      } else {
        col.title = computed;
      }
    });

    props.onChange('change-all', { row: rowIndex, col: colIndex }, undefined, rows);
  }

  function handleSetting(value: CellSetting, rowIndex: number, colIndex: number): void {
    props.onChange('setting-cell', { row: rowIndex, col: colIndex }, value);
  }

  function isReadonly(rowIndex: number, colIndex: number): boolean {
    const readonly = rowIndex < props.gridRowTitle || colIndex < props.gridColTitle;
    if (props.mode === Mode.DESIGN) {
      return !readonly;
    }
    return readonly;
  }

  const checkNumberValid = (value: string): string => {
    let cellValue = value;
    // 不能输入数字，小数点，负号之外的字符
    cellValue = cellValue.replace(/[^-\d.]/g, '');
    // 必须保证第一位为数字或负号而不是.
    cellValue = cellValue.replace(/^\./g, '');
    // 保证只有出现一个小数点而没有多个小数点
    cellValue = cellValue.replace(/\.{2,}/g, '.');
    // 保证小数点只出现一次，而不能出现两次以上
    cellValue = cellValue.replace('.', '$#$').replace(/\./g, '').replace('$#$', '.');
    return cellValue;
  };

  return (
    <tbody>
      {rows.map((row, rowIndex) => (
        <tr key={rowIndex}>
          <Block if={props.mode === Mode.DESIGN}>
            <td className={styles.head}>
              <RowAction index={rowIndex} length={props.rows} onAction={props.onChange} />
            </td>
          </Block>
          {row.map((column, colIndex) => (
            <td key={`${rowIndex}_${colIndex}`}
              className={service.getClassName(rowIndex, colIndex, props.gridRowTitle, props.gridColTitle)}>
              <Block if={props.type === 'input'}>
                <input
                  value={column.title}
                  type={column.type ?? 'text'}
                  readOnly={isReadonly(rowIndex, colIndex) || column.empty === 'yes' || column.enable === 'disabled'}
                  placeholder={column.placeholder}
                  onChange={event => handleChange(column, event.target.value, rowIndex, colIndex)}
                />
              </Block>
              <Block if={props.type === 'setting'}>
                <div className={styles.setting} onClick={() => handleSetting(column, rowIndex, colIndex)}>
                  {column.placeholder == null || column.placeholder.length === 0 ? t('点击设置') : column.placeholder}
                </div>
              </Block>
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}
